/* Plain formatting helpers. Everything here renders real values only. */

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

export function formatBytes(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "–";
  if (n < 1024) return `${n} B`;
  let i = 0;
  let v = n;
  while (v >= 1024 && i < UNITS.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 ? Math.round(v) : v.toFixed(digits).replace(/\.0$/, "")} ${UNITS[i]}`;
}

export function formatPercent(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "–";
  const p = ratio * 100;
  if (p > 0 && p < 1) return "<1%";
  return `${Math.round(p)}%`;
}

export function ratio(used: number, total: number): number | null {
  return total > 0 ? used / total : null;
}

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "short" });

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "–";
  const s = Math.round((t - now) / 1000);
  const a = Math.abs(s);
  if (a < 45) return "just now";
  if (a < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (a < 86400) return rtf.format(Math.round(s / 3600), "hour");
  if (a < 86400 * 7) return rtf.format(Math.round(s / 86400), "day");
  return formatDate(iso, { withYear: new Date(t).getFullYear() !== new Date(now).getFullYear() });
}

export function formatDate(iso: string | null | undefined, opts: { withYear?: boolean; withTime?: boolean } = {}): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("en", {
    month: "short",
    day: "numeric",
    ...(opts.withYear ? { year: "numeric" } : {}),
    ...(opts.withTime ? { hour: "numeric", minute: "2-digit" } : {}),
  });
}

/** "Dec 31, 2025" for a calendar date ("2025-12-31"), without shifting it into the viewer's time zone. */
export function calendarDate(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return formatDate(day, { withYear: true });
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("en", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" });
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "–";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

export function daysUntil(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((t - now) / 86_400_000);
}

export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(now) - start(d)) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en", {
    weekday: "long",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

export function hostnameOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/* ── Milestone 3: backups ─────────────────────────────────────────── */

const rtfLong = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "long" });

/** "3 hours ago", spelled out, for sentences. */
export function relativeTimeLong(iso: string, now = Date.now()): string {
  const s = Math.round((Date.parse(iso) - now) / 1000);
  const a = Math.abs(s);
  if (a < 45) return "just now";
  if (a < 3600) return rtfLong.format(Math.round(s / 60), "minute");
  if (a < 86400) return rtfLong.format(Math.round(s / 3600), "hour");
  return rtfLong.format(Math.round(s / 86400), "day");
}

/** "3:12 AM", in the viewer's time zone. */
export function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" });
}

/** "Oct 12, 3:12 AM" (with the year when it isn't this year). */
export function dateTimeShort(iso: string, now = new Date()): string {
  const d = new Date(iso);
  return d.toLocaleString("en", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "3h ago", "12m ago", "2d ago": for tight spaces like environment cards. */
export function shortAgo(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/**
 * When something next happens, the way people say it: "tonight at 3:12 AM",
 * "today at 2:30 PM", "tomorrow at 9:00 AM", "on Sunday at 5:12 AM".
 */
export function whenWords(iso: string, nowMs = Date.now()): string {
  const d = new Date(iso);
  const now = new Date(nowMs);
  const dayStart = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((dayStart(d) - dayStart(now)) / 86_400_000);
  const t = timeOfDay(iso);
  const h = d.getHours();
  // Small hours count as tonight, whether they fall before or after midnight.
  if ((days === 0 && (h >= 18 || (h < 5 && now.getHours() < 5))) || (days === 1 && h < 5 && now.getHours() >= 5)) return `tonight at ${t}`;
  if (days === 0) return `today at ${t}`;
  if (days === 1) return `tomorrow at ${t}`;
  if (days > 1 && days < 7) return `on ${d.toLocaleDateString("en", { weekday: "long" })} at ${t}`;
  return `on ${d.toLocaleDateString("en", { month: "short", day: "numeric" })} at ${t}`;
}

/** For something due soon: "now" (or overdue), "in a few minutes", "in 25 minutes"; null when it's an hour or more away. */
export function soonWords(iso: string, nowMs = Date.now()): string | null {
  const min = (Date.parse(iso) - nowMs) / 60_000;
  if (min <= 2) return "now";
  if (min < 10) return "in a few minutes";
  if (min < 60) return `in ${Math.round(min)} minutes`;
  return null;
}

/** "a, b and c" */
export function listWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** "1.2 GB, 14 MB new" */
export function sizeWords(total: number | null | undefined, added: number | null | undefined): string | null {
  if (total == null) return null;
  return added != null ? `${formatBytes(total)}, ${formatBytes(added)} new` : formatBytes(total);
}
